<template>
  <div class='dns-issue-body'>
    <div class="description-block">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container description">
        {{description}}
      </div>
    </div>
    <div class="tld-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <div class="technical-block" v-if='displayTechnicalSection'>
      <div class="data-title">{{$t('issueDataPopup.technical.data')}}</div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='details-column technical-data' v-if='additionalInfo'>
            <div class="data-sub-title">{{$t('detailed.issue.additional.info.title')}}</div>
            <div class='text-container additional-info'>{{additionalInfo}}</div>
          </div>
          <div class='details-column technical-data' v-if='cveName'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.name')}}</div>
            <div class='text-container cve-name'>{{ cveName }}</div>
          </div>
          <div class='details-column technical-data' v-if='cvssScore'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.cvss')}}</div>
            <div class='text-container cvss-score' :style="cvssStyle">{{ cvssScore }}</div>
          </div>
          <div class='details-column technical-data' v-if='epssScore'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.epss')}}</div>
            <div class='text-container cve-epss'>
              <epss-score :value='epssFormattedScore'/>
            </div>
          </div>
          <span class="url-block" v-if='urls.length'>
            <div class="data-sub-title">{{$t('issueDataPopup.technologies.cve.url')}}</div>
              <div v-for='(url, index) in urls' :key='index'>
               <div class='url-option'>
                <div class='data-title option'>
                  <span class="material-icons bullet">
                    lens_blur
                  </span>
                </div>
               <div class='text-container'>
                 <a :href='url' target="_blank"> {{url}} </a>
               </div>
              </div>
            </div>
          </span>
        </div>
      </div>
    </div>
    <span class="solution-block" v-if='solutions.length'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
        <div v-for='(solution, index) in solutions' :key='index'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
         <div class='text-container'>{{solution}}</div>
        </div>
      </div>
    </span>
  </div>
</template>

<script>
import EpssScore from '@/components/common/EpssScore.vue';

export default {
  name: 'DnsIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  components: {
    EpssScore,
  },
  computed: {
    cvssStyle() {
      return {
        ...this.color,
        backgroundColor: this.color['background-color'],
        fontSize: '14px',
        width: '37px',
        height: '25px',
        borderRadius: '3px',
      };
    },
    color() {
      const severity = this.cvssScore;
      if (severity >= 9 && severity <= 10) {
        return {color: '#E0255E', 'background-color': '#E0255030'};
      }
      if (severity >= 7 && severity <= 8.9) {
        return {color: '#FF7F00', 'background-color': '#FF7F0030'};
      }
      if (severity >= 4 && severity <= 6.9) {
        return {color: '#2459d5', 'background-color': '#2459d530'};
      }
      return {color: '#2BAF69', 'background-color': '#2BAF6030'};
    },
    metaData() {
      return this.issue.metaData || {};
    },
    description() {
      return this.metaData.findingsData?.description || this.issue.description || '';
    },
    // Asset with specified port
    target() {
      return this.metaData.target || '';
    },
    tld() {
      return this.issue.tld;
    },
    cveName() {
      return this.metaData.cveName || '';
    },
    cvssScore() {
      return this.metaData.cvss || '';
    },
    epssScore() {
      return this.metaData.epss || '';
    },
    epssFormattedScore() {
      let epss = this.epssScore || 0;
      epss = Math.round(((epss * 100) + Number.EPSILON) * 100) / 100;
      return epss === 0 ? '' : epss;
    },
    cveUrl() {
      return this.metaData.cveUrl || '';
    },
    kbUrl() {
      return this.metaData?.kbUrl || '';
    },
    urls() {
      const urlList = [];
      if (this.cveUrl !== '') {
        urlList.push(this.cveUrl);
      }
      if (this.kbUrl !== '') {
        urlList.push(this.kbUrl);
      }
      return urlList;
    },
    displayTechnicalSection() {
      return this.cveName || this.cvssScore || this.epssScore || this.cveUrl;
    },
    solutions() {
      return this.metaData.findingsData?.solutions || [];
    },
    additionalInfo() {
      return this.issue.severity !== 'informative' ? this.metaData.findingsData?.AdditionalInfo?.[0] : null;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.dns-issue-body {
  .cve-url {
    max-width: 95%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(650);
    margin-right: $space-4;
  }
  .technical-block {
    padding-bottom: $space-5;
    display: block;
    .technical-data {
      padding-top: $space-1;
    }
  }
  .url-block {
    display: block;
    padding-top: $space-2;
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .url-option {
      display: flex;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .cvss-score {
    margin-left: rem(17);
    text-align: center;
    font-family: $font-family-semibold;
    font-size: $font-size-base;
    justify-content: center;
    letter-spacing: 0;
    line-height: rem(16);
    padding: rem(5) rem(5) rem(5) rem(6);
  }
  .epss-score {
    margin-top: $space-3;
    align-items: center;
    display: flex;
    max-width: rem(70);
    margin-left: rem(1);
  }
  .additional-info {
    margin-bottom: $space-3;
  }
  .tld-block {
    margin-bottom: $space-5;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .solution-block {
    display: block;
    padding-bottom: $space-5;
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .description-block {
    display: block;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: $space-15;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-5;
    }
    .description {
      white-space: pre-line;
    }
  }
}
</style>
